import { useState, useCallback, useEffect, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLogin } from "@/api/hooks/useAuth";
import { useTwoFactorLogin } from "@/api/hooks/useTwoFactor";
import { getErrorMessage } from "@/api/client";
import { LoginRequestSchema, type LoginRequest } from "@/api/types/auth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Monogram, ProductName } from "@/components/ui/Brand";

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const login = useLogin();
  const twoFactorLogin = useTwoFactorLogin();

  // Opt-in 2FA: a normal (unenrolled) account never leaves this null — the
  // form below renders and behaves exactly as it did before 2FA existed.
  // Only a 2FA-enrolled account's login response sets a challenge, which
  // swaps the form for the code step.
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");

  const rawReturn = searchParams.get("return") ?? "/contacts";
  const returnUrl = rawReturn.startsWith("/") ? rawReturn : "/contacts";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: { email: "", password: "" },
  });

  useEffect(() => {
    setError(null);
  }, []);

  const onSubmit = useCallback(
    async (form: LoginRequest) => {
      setError(null);
      try {
        const outcome = await login.mutateAsync(form);
        if (outcome.totpRequired) {
          setChallenge(outcome.challenge);
          return;
        }
        navigate(returnUrl, { replace: true });
      } catch (err) {
        setError(getErrorMessage(err));
      }
    },
    [login, navigate, returnUrl],
  );

  const onSubmitCode = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!challenge) return;
      setError(null);
      try {
        await twoFactorLogin.mutateAsync({ challenge, code });
        navigate(returnUrl, { replace: true });
      } catch (err) {
        setError(getErrorMessage(err));
      }
    },
    [challenge, code, twoFactorLogin, navigate, returnUrl],
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-dawn p-6">
      <div className="w-full max-w-[400px]">
        <div className="mb-8 flex flex-col items-center text-center">
          <Monogram className="text-xl sm:text-2xl" />
          <ProductName className="mt-2" />
          <p className="text-ink-soft mt-3 text-sm">Sign in to manage the venue</p>
        </div>

        {challenge === null ? (
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="space-y-5 bg-bg-card border border-border rounded-xl p-6 shadow-sm"
          >
            {error && (
              <div className="rounded-md bg-danger-light border border-danger/30 px-4 py-3">
                <p className="text-sm font-medium text-danger">{error}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@thefoxandtheowl.net"
                error={!!errors.email}
                {...register("email")}
              />
              {errors.email && <p className="text-sm text-danger">{errors.email.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                error={!!errors.password}
                {...register("password")}
              />
              {errors.password && <p className="text-sm text-danger">{errors.password.message}</p>}
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={login.isPending}>
              {login.isPending ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        ) : (
          <form
            onSubmit={onSubmitCode}
            noValidate
            className="space-y-5 bg-bg-card border border-border rounded-xl p-6 shadow-sm"
          >
            <p className="text-sm text-text-secondary">
              Enter the 6-digit code from your authenticator app.
            </p>

            {error && (
              <div className="rounded-md bg-danger-light border border-danger/30 px-4 py-3">
                <p className="text-sm font-medium text-danger">{error}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="totp-code">Authentication code</Label>
              <Input
                id="totp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                autoFocus
              />
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={twoFactorLogin.isPending || code.length < 6}>
              {twoFactorLogin.isPending ? "Verifying..." : "Verify"}
            </Button>

            <button
              type="button"
              className="w-full text-center text-sm text-text-secondary hover:text-text-primary transition-colors"
              onClick={() => {
                setChallenge(null);
                setCode("");
                setError(null);
              }}
            >
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
